/**
 * Currency utilities for frontend
 *
 * Symbol catalogue mirrors backend `services/currency_catalogue.py` so a
 * label rendered server-side and a label rendered here look identical.
 * Add new codes here when you add them backend-side; the FX rate machinery
 * is independent of this list (rates come from the API).
 */
import api from './api';
import { getCurrencyMeta } from './currencies';

const CURRENCY_SYMBOLS = {
  // Global staples
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$',
  JPY: '¥', CNY: '¥', CHF: 'CHF', PLN: 'zł', INR: '₹',
  BRL: 'R$', MXN: 'MX$', AED: 'د.إ', SAR: '﷼',
  // Africa — Southern + East
  ZAR: 'R', ZIG: 'ZIG', ZWG: 'ZWG', ZWL: 'Z$',
  NGN: '₦', KES: 'KSh', GHS: 'GH₵', UGX: 'USh', TZS: 'TSh',
  BWP: 'P', NAD: 'N$', MWK: 'MK', MZN: 'MT', AOA: 'Kz', ZMW: 'ZK',
  RWF: 'RF', ETB: 'Br', BIF: 'FBu', LSL: 'L', SZL: 'E',
  MGA: 'Ar', MUR: '₨', EGP: 'E£', MAD: 'DH',
};

const DECIMAL_PLACES = { JPY: 0, KRW: 0, default: 2 };

let ratesCache = null;
let ratesCacheTimestamp = null;

export function getCurrencySymbol(code) {
  const c = code?.toUpperCase();
  return CURRENCY_SYMBOLS[c] || getCurrencyMeta(c)?.symbol || code || '$';
}

export function getDecimalPlaces(code) {
  return DECIMAL_PLACES[code?.toUpperCase()] ?? DECIMAL_PLACES.default;
}

export function formatCurrency(amount, currencyCode = 'USD', options = {}) {
  const code = currencyCode?.toUpperCase() || 'USD';
  const symbol = getCurrencySymbol(code);
  const decimals = options.decimals ?? getDecimalPlaces(code);
  if (amount === null || amount === undefined || isNaN(amount)) {
    return options.showCode ? `${symbol}0 ${code}` : `${symbol}0`;
  }
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(amount);
  return options.showCode ? `${symbol}${formatted} ${code}` : `${symbol}${formatted}`;
}

/**
 * Shorter alias used widely across dashboards. Renders `R1,234.56` /
 * `$1,234.56` etc. Keeps a single canonical formatter so accounting,
 * giving, fees, bank ledger and church public pages all match.
 */
export const fmtAmount = (amount, code = 'USD', options = {}) =>
  formatCurrency(amount, code, options);

/**
 * Grouped number WITHOUT a currency symbol — for screens that print the
 * code separately or hold mixed-currency rows. Lives here so components
 * never reach for `Intl.NumberFormat` directly (it throws on non-ISO
 * codes such as Zimbabwe's ZWG / ZIG).
 */
export function formatNumber(amount, decimals = 2) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return (0).toFixed(decimals);
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

/**
 * Compact rendering used for KPI tiles (e.g. R1.2M, $4.8k). Negative
 * values keep a leading minus sign.
 */
export function fmtAmountCompact(amount, code = 'USD') {
  if (amount === null || amount === undefined || isNaN(amount)) return formatCurrency(0, code);
  const abs = Math.abs(amount);
  const sym = getCurrencySymbol(code?.toUpperCase() || 'USD');
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}${sym}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}${sym}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${sym}${(abs / 1_000).toFixed(1)}k`;
  return formatCurrency(amount, code);
}

export async function fetchExchangeRates(baseCurrency = 'USD', forceRefresh = false) {
  const cacheKey = baseCurrency.toUpperCase();
  if (!forceRefresh && ratesCache?.[cacheKey] && ratesCacheTimestamp) {
    if (Date.now() - ratesCacheTimestamp < 300000) return ratesCache[cacheKey];
  }
  try {
    const response = await api.get(`/admin/exchange-rates?base=${baseCurrency}`);
    if (!ratesCache) ratesCache = {};
    ratesCache[cacheKey] = response.data;
    ratesCacheTimestamp = Date.now();
    return response.data;
  } catch (error) {
    return ratesCache?.[cacheKey] || { base: baseCurrency, rates: {}, error: 'Failed' };
  }
}

export async function convertCurrency(amount, fromCurrency, toCurrency) {
  if (fromCurrency?.toUpperCase() === toCurrency?.toUpperCase()) {
    return { original: amount, converted: amount, rate: 1 };
  }
  try {
    const response = await api.post('/admin/currency/convert', {
      amount, from_currency: fromCurrency, to_currency: toCurrency
    });
    return { original: amount, converted: response.data.converted_amount, rate: response.data.rate };
  } catch (error) {
    const rates = await fetchExchangeRates(fromCurrency);
    const rate = rates.rates?.[toCurrency?.toUpperCase()];
    if (!rate) throw new Error(`No rate for ${fromCurrency} to ${toCurrency}`);
    return { original: amount, converted: Math.round(amount * rate * 100) / 100, rate };
  }
}

export function convertCurrencySync(amount, fromCurrency, toCurrency, cachedRates = null) {
  if (fromCurrency?.toUpperCase() === toCurrency?.toUpperCase()) return amount;
  const rates = cachedRates || ratesCache?.[fromCurrency?.toUpperCase()]?.rates;
  const rate = rates?.[toCurrency?.toUpperCase()];
  if (!rate) return amount;
  return Number((amount * rate).toFixed(getDecimalPlaces(toCurrency)));
}

export default { getCurrencySymbol, getDecimalPlaces, formatCurrency, fmtAmount, fmtAmountCompact, formatNumber, fetchExchangeRates, convertCurrency, convertCurrencySync };
